import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Users, Check } from 'lucide-react';
import { useTeamContext } from '../../hooks/useTeamContext';

export function TeamSwitcher() {
  const { teams, activeTeam, activeTeamId, hasMultipleTeams, setActiveTeam, isLoading } = useTeamContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if user only has one team or no teams
  if (isLoading || !hasMultipleTeams) {
    return null;
  }

  const handleTeamSelect = async (teamId: string) => {
    await setActiveTeam(teamId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white
                   border border-gray-light rounded-card hover:bg-gray-50 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <Users size={16} className="text-gray-medium" />
        <span className="max-w-[150px] truncate">
          {activeTeam?.name || 'Select Team'}
        </span>
        <ChevronDown
          size={16}
          className={`text-gray-medium transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white rounded-card shadow-lg border border-gray-light z-50"
          role="listbox"
          aria-label="Select team"
        >
          <div className="py-2">
            <div className="px-4 py-2 text-xs font-semibold text-gray-medium uppercase tracking-wider">
              Switch Team
            </div>
            {teams.map(team => (
              <button
                key={team.teamId}
                onClick={() => handleTeamSelect(team.teamId)}
                className={`
                  w-full flex items-center justify-between px-4 py-2 text-sm text-left
                  hover:bg-gray-50 transition-colors
                  ${activeTeamId === team.teamId ? 'bg-coral/5 text-coral' : 'text-gray-700'}
                `}
                role="option"
                aria-selected={activeTeamId === team.teamId}
              >
                <div className="flex items-center gap-3">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold
                    ${activeTeamId === team.teamId ? 'bg-coral text-white' : 'bg-gray-100 text-gray-600'}
                  `}>
                    {team.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium">{team.name}</div>
                    <div className="text-xs text-gray-medium">
                      {team.members?.length || 0} member{(team.members?.length || 0) !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                {activeTeamId === team.teamId && (
                  <Check size={16} className="text-coral flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
